import React, { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  TicketIcon,
  UserIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { v4 as uuidv4 } from "uuid";
import scannerApi from "../apis/scanner.api";

const getPayload = (response) => {
  const body = response?.data || {};
  if (
    body?.data &&
    body.valid === undefined &&
    (body.data.valid !== undefined || body.data.bookingData)
  ) {
    return body.data;
  }
  return body;
};

const getErrorPayload = (error) =>
  getPayload({ data: error?.response?.data || {} });

const getAttendeeName = (attendee) =>
  attendee?.name ||
  [attendee?.firstName, attendee?.lastName].filter(Boolean).join(" ") ||
  "";

const getPersonSummary = (person) => {
  if (!person) return "";
  const name = person.name || getAttendeeName(person);
  if (name && person.email) return `${name} · ${person.email}`;
  return name || person.email || "";
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const statusStyle = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "valid" || normalized === "accepted") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (normalized === "used" || normalized === "fully_scanned") {
    return "border-amber-200 bg-amber-50 text-amber-800";
  }
  if (normalized === "attendee_pending") {
    return "border-orange-200 bg-orange-50 text-orange-800";
  }
  return "border-red-200 bg-red-50 text-red-700";
};

const statusLabel = (status) => {
  const labels = {
    valid: "Ready to check in",
    accepted: "Check-in complete",
    used: "Already scanned",
    fully_scanned: "Booking fully scanned",
    attendee_pending: "Attendee details required",
    named_ticket_required: "Individual ticket required",
    invalid_payment_status: "Payment not complete",
    expired: "Expired ticket",
    void: "Void ticket",
    refunded: "Refunded ticket",
    invalid: "Invalid ticket",
  };
  return labels[String(status || "").toLowerCase()] || "Ticket unavailable";
};

const operatorInstruction = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "named_ticket_required") {
    return "Ask the guest to open an individual ticket QR and scan that code.";
  }
  if (normalized === "attendee_pending") {
    return "Attendee details must be completed before entry.";
  }
  return "";
};

const buildPreview = (payload, qrCodeData) => {
  const booking = payload?.bookingData || {};
  const ticket = booking?.ticket_instance || payload?.ticket || null;
  const individual =
    String(qrCodeData || "").startsWith("tkt_") || Boolean(ticket);
  const quantity = Number(booking?.quantity || 0);
  const used = Number(booking?.scan_count || 0);
  const remaining = individual
    ? Number(payload?.remaining ?? (payload?.valid ? 1 : 0))
    : Number(payload?.remaining ?? Math.max(0, quantity - used));

  return {
    payload,
    booking,
    ticket,
    individual,
    remaining: Math.max(0, remaining),
    status: payload?.status || (payload?.valid ? "valid" : "invalid"),
  };
};

function Detail({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div
      role="group"
      aria-label={`${label}: ${value}`}
      className="min-w-0"
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        {label}
      </p>
      <p className="mt-1 break-words text-sm font-semibold text-gray-950">
        {value}
      </p>
    </div>
  );
}

function DuplicateDetails({ duplicateScan }) {
  if (!duplicateScan) return null;

  const ticket = duplicateScan.ticket || {};
  const booking = duplicateScan.booking || {};
  const recentScans = duplicateScan.recentScans || [];
  const usedTickets = duplicateScan.usedTickets || [];

  return (
    <div className="mt-4 space-y-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
        <div>
          <p className="font-bold text-amber-950">Duplicate scan warning</p>
          <p className="mt-1 text-sm text-amber-800">
            Verify the details below before allowing entry.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Detail label="Scanned at" value={formatDateTime(duplicateScan.scannedAt)} />
        <Detail
          label="Scanned by"
          value={getPersonSummary(duplicateScan.scannedBy)}
        />
        <Detail label="Booking" value={booking.bookingId} />
        <Detail
          label="Buyer"
          value={getPersonSummary({
            name: booking.buyerName,
            email: booking.buyerEmail,
          })}
        />
        <Detail label="Ticket" value={ticket.ticketName} />
        <Detail label="Seat" value={ticket.seatNo} />
        <Detail label="Attendee" value={getPersonSummary(ticket.attendee)} />
      </div>

      {recentScans.length > 0 && (
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-amber-900">
            Recent booking scans
          </p>
          <div className="mt-2 space-y-2">
            {recentScans.slice(0, 3).map((scan, index) => (
              <div
                key={scan.id || `${scan.scannedAt}-${index}`}
                className="flex flex-wrap justify-between gap-2 rounded-md bg-white/70 px-3 py-2 text-xs text-amber-950"
              >
                <span>{formatDateTime(scan.scannedAt)}</span>
                <span>{scan.scannedBy?.name || scan.scannedBy?.email}</span>
                <span>{scan.scannedCount || 0} admitted</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {usedTickets.length > 0 && (
        <p className="text-xs font-semibold text-amber-900">
          {usedTickets.length} individual ticket
          {usedTickets.length === 1 ? "" : "s"} already used.
        </p>
      )}
    </div>
  );
}

export default function ScanPreviewConfirmModal({
  isOpen,
  onClose,
  qrCodeData,
  bookingId,
  onConfirmed,
  onError,
}) {
  const scannedValue = qrCodeData || bookingId || "";
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState("");
  const [count, setCount] = useState(1);
  const [networkRetry, setNetworkRetry] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const idempotencyKeyRef = useRef(null);

  const reset = () => {
    setLoading(false);
    setConfirming(false);
    setPreview(null);
    setMessage("");
    setCount(1);
    setNetworkRetry(false);
    setAccepted(false);
    idempotencyKeyRef.current = null;
  };

  useEffect(() => {
    if (!isOpen || !scannedValue) {
      if (!isOpen) reset();
      return;
    }

    setLoading(true);
    setMessage("");
    setPreview(null);
    setCount(1);
    setNetworkRetry(false);
    setAccepted(false);
    idempotencyKeyRef.current = null;

    scannerApi.verifyTicket({
      payload: { qrCodeData: scannedValue },
      success: (response) => {
        const payload = getPayload(response);
        const nextPreview = buildPreview(payload, scannedValue);
        setPreview(nextPreview);
        setCount(1);
        setMessage(payload?.message || "");
        setLoading(false);
      },
      error: (error) => {
        const payload = getErrorPayload(error);
        if (
          payload?.status ||
          payload?.valid !== undefined ||
          payload?.duplicateScan
        ) {
          setPreview(buildPreview(payload, scannedValue));
        }
        setMessage(
          payload?.message ||
            payload?.error ||
            "Unable to verify this ticket.",
        );
        setLoading(false);
        onError?.(error);
      },
    });
  }, [isOpen, onError, scannedValue]);

  const closeModal = () => {
    reset();
    onClose?.();
  };

  const confirmTicket = () => {
    if (!preview?.payload?.valid || confirming || accepted) return;

    const key = idempotencyKeyRef.current || uuidv4();
    idempotencyKeyRef.current = key;
    setConfirming(true);
    setMessage("");
    setNetworkRetry(false);

    const payload = {
      qrCodeData: scannedValue,
      idempotencyKey: key,
    };
    if (!preview.individual) {
      payload.count = Math.max(
        1,
        Math.min(Number(count || 1), preview.remaining),
      );
    }

    scannerApi.confirmTicket({
      payload,
      success: (response) => {
        const body = getPayload(response);
        if (body?.valid === false) {
          setPreview(buildPreview(body, scannedValue));
          setMessage(body?.message || body?.error || "Entry was not accepted.");
          setConfirming(false);
          idempotencyKeyRef.current = null;
          return;
        }

        setConfirming(false);
        setAccepted(true);
        const scannedNow = Number(
          body?.scannedNow || (preview.individual ? 1 : count),
        );
        setPreview((current) => {
          const responsePreview = buildPreview(body, scannedValue);
          const hasResponseBooking =
            Object.keys(responsePreview.booking || {}).length > 0;
          return {
            ...current,
            payload: body,
            booking: hasResponseBooking
              ? responsePreview.booking
              : current.booking,
            ticket: responsePreview.ticket || current.ticket,
            remaining:
              body?.remaining === undefined || body?.remaining === null
                ? Math.max(0, current.remaining - scannedNow)
                : Math.max(0, Number(body.remaining)),
            status:
              body?.status ||
              (current.individual ? "used" : current.status),
          };
        });
        setMessage(
          body?.idempotentReplay
            ? "This scan was already accepted."
            : body?.message || "Ticket checked in.",
        );
        onConfirmed?.({
          qrCodeData: scannedValue,
          ticketInstanceId:
            body?.bookingData?.ticket_instance?.ticketInstanceId ||
            preview.ticket?.ticketInstanceId,
          scannedNow,
          idempotentReplay: Boolean(body?.idempotentReplay),
          serverResponse: response,
        });
        idempotencyKeyRef.current = null;
      },
      error: (error) => {
        const body = getErrorPayload(error);
        const hasBusinessResponse = Boolean(error?.response);

        if (
          body?.status ||
          body?.valid !== undefined ||
          body?.duplicateScan
        ) {
          setPreview(buildPreview(body, scannedValue));
        }

        setNetworkRetry(!hasBusinessResponse);
        setMessage(
          body?.message ||
            body?.error ||
            "Network error. Retry will safely use the same request key.",
        );
        setConfirming(false);

        if (hasBusinessResponse) {
          idempotencyKeyRef.current = null;
        }
        onError?.(error);
      },
    });
  };

  const ticket = preview?.ticket || {};
  const booking = preview?.booking || {};
  const duplicateScan = preview?.payload?.duplicateScan;
  const visibleTicket = ticket?.ticketName
    ? ticket
    : duplicateScan?.ticket || ticket;
  const attendee = visibleTicket?.attendee || {};
  const canConfirm = Boolean(
    !accepted && preview?.payload?.valid && preview.remaining > 0,
  );
  const displayStatus = accepted ? "accepted" : preview?.status;
  const instruction = operatorInstruction(preview?.status);
  const showTicketIdentity = Boolean(
    preview?.individual &&
      (visibleTicket?.ticketName ||
        visibleTicket?.seatNo ||
        visibleTicket?.label),
  );

  return (
    <Transition appear show={Boolean(isOpen)} as={Fragment}>
      <Dialog as="div" className="relative z-[70]" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-950/65 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-0 sm:items-center sm:p-5">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="translate-y-6 opacity-0 sm:scale-95"
              enterTo="translate-y-0 opacity-100 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="translate-y-0 opacity-100 sm:scale-100"
              leaveTo="translate-y-6 opacity-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-xl overflow-hidden rounded-t-lg bg-white shadow-2xl sm:rounded-lg">
                <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4 sm:px-6">
                  <div>
                    <Dialog.Title className="text-lg font-bold text-gray-950">
                      Ticket verification
                    </Dialog.Title>
                    <p className="mt-1 max-w-md break-all text-xs text-gray-500">
                      {scannedValue}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                    aria-label="Close verification"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="max-h-[75vh] overflow-y-auto px-5 py-5 sm:px-6">
                  {loading ? (
                    <div className="flex min-h-48 items-center justify-center">
                      <div className="text-center">
                        <div className="mx-auto h-9 w-9 animate-spin rounded-full border-4 border-gray-200 border-t-[#1d275f]" />
                        <p className="mt-3 text-sm font-medium text-gray-500">
                          Verifying ticket...
                        </p>
                      </div>
                    </div>
                  ) : preview ? (
                    <>
                      {showTicketIdentity && (
                        <section
                          aria-label="Scanned ticket"
                          className="rounded-lg bg-[#1d275f] p-5 text-white"
                        >
                          <p className="text-xs font-bold uppercase tracking-wide text-white/65">
                            Scanned ticket
                          </p>
                          <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0">
                              <h2 className="break-words text-2xl font-black leading-tight sm:text-3xl">
                                {visibleTicket.ticketName || "Ticket"}
                              </h2>
                              {visibleTicket.label && (
                                <p className="mt-2 text-sm font-semibold text-white/75">
                                  {visibleTicket.label}
                                </p>
                              )}
                              {getAttendeeName(attendee) && (
                                <p className="mt-3 text-sm font-bold text-white">
                                  {getAttendeeName(attendee)}
                                </p>
                              )}
                            </div>
                            {visibleTicket.seatNo && (
                              <div
                                role="group"
                                aria-label={`Seat ${visibleTicket.seatNo}`}
                                className="w-fit shrink-0 rounded-lg bg-white px-5 py-3 text-[#1d275f] shadow-sm"
                              >
                                <p className="text-[11px] font-black uppercase tracking-wide text-gray-500">
                                  Seat
                                </p>
                                <p className="mt-1 text-2xl font-black leading-none">
                                  {visibleTicket.seatNo}
                                </p>
                              </div>
                            )}
                          </div>
                        </section>
                      )}

                      <div
                        className={`${showTicketIdentity ? "mt-3" : ""} rounded-lg border p-4 ${statusStyle(
                          displayStatus,
                        )}`}
                      >
                        <div className="flex items-start gap-3">
                          {canConfirm || accepted ? (
                            <CheckCircleIcon className="h-6 w-6 shrink-0" />
                          ) : (
                            <ExclamationTriangleIcon className="h-6 w-6 shrink-0" />
                          )}
                          <div>
                            <p className="font-bold">
                              {statusLabel(displayStatus)}
                            </p>
                            <p className="mt-1 text-sm">
                              {message ||
                                (canConfirm
                                  ? "Review the ticket before confirming entry."
                                  : "This ticket cannot be checked in.")}
                            </p>
                            {instruction && (
                              <p className="mt-2 text-sm font-black">
                                {instruction}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {(canConfirm || accepted) && (
                        <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
                          {!preview.individual && (
                            <div className="flex items-center gap-2">
                              <TicketIcon className="h-5 w-5 text-[#1d275f]" />
                              <p className="text-base font-bold text-gray-950">
                                {booking.title || "Legacy booking ticket"}
                              </p>
                            </div>
                          )}
                          <div
                            className={`${preview.individual ? "" : "mt-4"} grid grid-cols-2 gap-x-5 gap-y-4`}
                          >
                            <Detail label="Label" value={visibleTicket.label} />
                            <Detail
                              label="Attendee"
                              value={getAttendeeName(attendee)}
                            />
                            <Detail
                              label="Booking"
                              value={booking.booking_id}
                            />
                            <Detail
                              label="Buyer"
                              value={
                                [booking.first_name, booking.last_name]
                                  .filter(Boolean)
                                  .join(" ") || booking.email
                              }
                            />
                            <Detail
                              label="Remaining"
                              value={preview.remaining}
                            />
                          </div>
                        </div>
                      )}

                      {canConfirm && !preview.individual && (
                        <div className="mt-4 rounded-lg border border-gray-200 p-4">
                          <label
                            htmlFor="legacy-scan-count"
                            className="text-sm font-bold text-gray-900"
                          >
                            People entering now
                          </label>
                          <p className="mt-1 text-xs text-gray-500">
                            This count is available only for a legacy booking QR.
                          </p>
                          <input
                            id="legacy-scan-count"
                            type="number"
                            min="1"
                            max={preview.remaining}
                            value={count}
                            onChange={(event) =>
                              setCount(
                                Math.max(
                                  1,
                                  Math.min(
                                    Number(event.target.value || 1),
                                    preview.remaining,
                                  ),
                                ),
                              )
                            }
                            className="mt-3 w-28 rounded-md border border-gray-300 px-3 py-2 text-base font-bold outline-none focus:border-[#1d275f] focus:ring-2 focus:ring-[#1d275f]/10"
                          />
                        </div>
                      )}

                      <DuplicateDetails duplicateScan={duplicateScan} />

                      {networkRetry && (
                        <div className="mt-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                          <ClockIcon className="mt-0.5 h-4 w-4 shrink-0" />
                          Retry uses the same idempotency key, so the same entry
                          cannot be counted twice.
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                      {message || "No ticket information was returned."}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 border-t border-gray-100 bg-gray-50 px-5 py-4 sm:justify-end sm:px-6">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50 sm:flex-none"
                  >
                    Close
                  </button>
                  {canConfirm && (
                    <button
                      type="button"
                      onClick={confirmTicket}
                      disabled={confirming}
                      className="flex-1 rounded-md bg-[#1d275f] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#151d49] disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none"
                    >
                      {confirming
                        ? "Confirming..."
                        : networkRetry
                          ? "Retry check-in"
                          : preview.individual
                            ? "Check in ticket"
                            : `Check in ${count}`}
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
